import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { ExpandedState } from '@tanstack/react-table';
import ApiService from '../../../../../services/ApiService';
import { StandardDataTable, StandardColumn, useColumnsState } from '../../../../../components/StandardPage';
import { OverflowCell } from '../../../../../components/OverflowCell';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { Filter } from '../../../../../components/SubHeaderFilter/types';
import { matchFilter } from '../../../../../utils/filterMatcher';
import NcmDrawer, { NcmDrawerTab } from './NcmDrawer';
import CatalogView, { NcmDoCatalogo } from './CatalogView';
import { SITUACAO_LABEL, SITUACAO_TOM, emissorLabel, plural, usoPartes, usoTotal } from './constants';
import { EmissorResumo, NcmProfileItemDto, NcmProfilesListResponse, SituacaoRegra } from './types';
import {
  ChapterLabel,
  Chip,
  ChapterSummary,
  MutedText,
  UsageHint,
  UsageLink,
} from './style';

const ncmRulesScreenKey = 'tax_ncm_rules';

type Linha =
  | { kind: 'capitulo'; id: string; capitulo: string; itens: NcmProfileItemDto[]; subRows: Linha[] }
  | { kind: 'ncm'; id: string; item: NcmProfileItemDto };

const perfilPrincipal = (i: NcmProfileItemDto) =>
  i.perfis.find((p) => p.companyEmissorId === null) ?? i.perfis[0] ?? null;

// Bloqueia venda primeiro, depois parcial, sem regra e configurada; dentro de cada, pelo código.
const ordem = (i: NcmProfileItemDto): number =>
  i.bloqueiaVenda ? 0 : i.situacao === 'PARCIAL' ? 1 : i.situacao === 'SEM_REGRA' ? 2 : 3;

// Valor de cada campo do filtro do header (ncmRulesHeaders, TaxModule/index.tsx).
const valorDoFiltro = (i: NcmProfileItemDto, campo: string): string =>
  campo === 'capitulo' ? i.capitulo : campo === 'uso' ? (usoTotal(i) > 0 ? 'com' : 'sem') : campo === 'situacao' ? i.situacao : '';

export default function NcmRules(): React.ReactElement {
  const { refresh, search, ncmFilters } =
    useOutletContext<{ refresh?: boolean; search?: string; ncmFilters?: Filter[] }>() ?? {};
  const filtros = useMemo(() => ncmFilters ?? [], [ncmFilters]);
  const { setFeedback } = useContext(FeedbackContext);
  const [searchParams] = useSearchParams();

  const [dados, setDados] = useState<NcmProfilesListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [versao, setVersao] = useState(0);

  const vista = searchParams.get('vista') === 'catalogo' ? 'catalogo' : 'adotados';
  const [aberturaManual, setAberturaManual] = useState<Record<string, boolean>>({});
  const [aberto, setAberto] = useState<{ ncmId: number; tab: NcmDrawerTab } | null>(null);

  const notificar = useCallback(
    (message: string, ok: boolean) => setFeedback({ isOpen: true, message, statusOk: ok }),
    [setFeedback],
  );

  useEffect(() => {
    let ativo = true;
    setIsLoading(true);
    ApiService.fiscal
      .listNcmProfiles()
      .then((r) => {
        if (ativo) setDados(r.data);
      })
      .catch((error: unknown) => console.error('Erro ao buscar regras por NCM:', error))
      .finally(() => {
        if (ativo) setIsLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [refresh, versao]);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  const itens = useMemo(() => dados?.itens ?? [], [dados]);
  const emissores: EmissorResumo[] = useMemo(() => dados?.emissores ?? [], [dados]);
  const termo = (search ?? '').trim().toLowerCase();
  const digitos = termo.replace(/\D/g, '');
  const filtrado = !!termo || filtros.length > 0;

  const visiveis = useMemo(
    () =>
      itens.filter(
        (i) =>
          (!termo ||
            i.ncm.toLowerCase().includes(termo) ||
            (!!digitos && i.ncm.replace(/\D/g, '').includes(digitos)) ||
            (i.descricao ?? '').toLowerCase().includes(termo)) &&
          filtros.every((f) => matchFilter(valorDoFiltro(i, f.key), f)),
      ),
    [itens, termo, digitos, filtros],
  );

  const linhas = useMemo<Linha[]>(() => {
    const porCapitulo = new Map<string, NcmProfileItemDto[]>();
    visiveis.forEach((i) => porCapitulo.set(i.capitulo, [...(porCapitulo.get(i.capitulo) ?? []), i]));
    return [...porCapitulo.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([cap, lista]) => {
        const ordenada = [...lista].sort((a, b) => ordem(a) - ordem(b) || a.ncm.localeCompare(b.ncm));
        return {
          kind: 'capitulo' as const,
          id: `cap-${cap}`,
          capitulo: cap,
          itens: ordenada,
          subRows: ordenada.map((item) => ({ kind: 'ncm' as const, id: `ncm-${item.ncmId}`, item })),
        };
      });
  }, [visiveis]);

  // Abertos por padrão: os capítulos que bloqueiam venda; com busca ou filtro, todos.
  // O que o usuário abre ou fecha fica por cima do padrão até os filtros mudarem.
  useEffect(() => setAberturaManual({}), [termo, filtros]);
  const expanded = useMemo<Record<string, boolean>>(
    () => ({
      ...Object.fromEntries(
        linhas.map((l) => [l.id, l.kind === 'capitulo' && (filtrado || l.itens.some((i) => i.bloqueiaVenda))]),
      ),
      ...aberturaManual,
    }),
    [linhas, filtrado, aberturaManual],
  );
  const onExpandedChange = (next: ExpandedState) => {
    if (next === true) return setAberturaManual(Object.fromEntries(linhas.map((l) => [l.id, true])));
    setAberturaManual(Object.fromEntries(linhas.map((l) => [l.id, !!next[l.id]])));
  };
  const alternarCapitulo = (id: string) => setAberturaManual((m) => ({ ...m, [id]: !expanded[id] }));

  const abrir = useCallback((ncmId: number, tab: NcmDrawerTab) => setAberto({ ncmId, tab }), []);

  const columns = useMemo<StandardColumn<Linha>[]>(
    () => [
      {
        id: 'ncm',
        label: 'NCM',
        sortable: false,
        accessor: (l) => (l.kind === 'capitulo' ? l.capitulo : l.item.ncm),
        cell: (l) =>
          l.kind === 'capitulo' ? (
            <ChapterLabel>
              Capítulo {l.capitulo}
              <MutedText>{plural(l.itens.length, 'NCM', 'NCMs')}</MutedText>
            </ChapterLabel>
          ) : (
            <strong>{l.item.ncm}</strong>
          ),
      },
      {
        id: 'descricao',
        label: 'Descrição',
        sortable: false,
        accessor: (l) => (l.kind === 'capitulo' ? '' : l.item.descricao ?? ''),
        cell: (l) => {
          if (l.kind === 'ncm') return <OverflowCell text={l.item.descricao || '—'} maxWidth={240} />;
          const bloqueiam = l.itens.filter((i) => i.bloqueiaVenda).length;
          const conta = (s: SituacaoRegra) => l.itens.filter((i) => i.situacao === s && !i.bloqueiaVenda).length;
          return (
            <ChapterSummary>
              {bloqueiam > 0 && <Chip $tone="bad">{plural(bloqueiam, 'bloqueia venda', 'bloqueiam venda')}</Chip>}
              {conta('PARCIAL') > 0 && <Chip $tone="warn">{conta('PARCIAL')} parcial</Chip>}
              {conta('SEM_REGRA') > 0 && <Chip $tone="mute">{conta('SEM_REGRA')} sem regra</Chip>}
              {conta('CONFIGURADA') > 0 && (
                <Chip $tone="ok">{plural(conta('CONFIGURADA'), 'configurada', 'configuradas')}</Chip>
              )}
            </ChapterSummary>
          );
        },
      },
      {
        id: 'cstIPI',
        label: 'CST IPI',
        sortable: false,
        accessor: (l) => (l.kind === 'ncm' ? perfilPrincipal(l.item)?.cstIPI ?? '' : ''),
        cell: (l) =>
          l.kind === 'ncm' ? perfilPrincipal(l.item)?.cstIPI ?? <MutedText>—</MutedText> : null,
      },
      {
        id: 'aliquotaIPI',
        label: 'Alíq. IPI',
        sortable: false,
        accessor: (l) => (l.kind === 'ncm' ? perfilPrincipal(l.item)?.aliquotaIPI ?? '' : ''),
        cell: (l) => {
          if (l.kind !== 'ncm') return null;
          const a = perfilPrincipal(l.item)?.aliquotaIPI;
          return a != null ? `${String(a).replace('.', ',')}%` : <MutedText>—</MutedText>;
        },
      },
      {
        id: 'uso',
        label: 'Uso',
        sortable: false,
        accessor: (l) => (l.kind === 'ncm' ? usoTotal(l.item) : ''),
        cell: (l) => {
          if (l.kind !== 'ncm') return null;
          const i = l.item;
          if (usoTotal(i) === 0) return <MutedText>sem uso</MutedText>;
          const [principal, ...resto] = usoPartes(i);
          return (
            <div>
              <UsageLink
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  abrir(i.ncmId, 'vinculos');
                }}
              >
                {principal}
              </UsageLink>
              {resto.length > 0 && <UsageHint>{resto.join(' · ')}</UsageHint>}
            </div>
          );
        },
      },
      {
        id: 'situacao',
        label: 'Situação',
        sortable: false,
        accessor: (l) => (l.kind === 'ncm' ? SITUACAO_LABEL[l.item.situacao] : ''),
        cell: (l) => {
          if (l.kind !== 'ncm') return null;
          const i = l.item;
          return (
            <div>
              <Chip $tone={SITUACAO_TOM[i.situacao]}>{SITUACAO_LABEL[i.situacao]}</Chip>
              {i.bloqueiaVenda && <UsageHint $bad>bloqueia venda</UsageHint>}
              {i.situacao === 'PARCIAL' && (
                <UsageHint>falta {i.emissoresSemRegra.map(emissorLabel).join(', ')}</UsageHint>
              )}
            </div>
          );
        },
      },
    ],
    [abrir],
  );

  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(ncmRulesScreenKey, columns);

  // NCM do catálogo que a empresa ainda não tem: painel com a regra em branco, nada gravado até salvar.
  const [doCatalogo, setDoCatalogo] = useState<NcmProfileItemDto | null>(null);
  const abrirDoCatalogo = (n: NcmDoCatalogo) => {
    setDoCatalogo({
      ncmId: n.id,
      ncm: n.ncm,
      descricao: n.descricao,
      capitulo: n.ncm.slice(0, 2),
      situacao: 'SEM_REGRA',
      emissoresSemRegra: emissores,
      bloqueiaVenda: false,
      usos: { resources: 0, orderItems: 0, naturezas: 0, produtos: 0, estoques: 0 },
      perfis: [],
      ultimaAlteracao: null,
    });
    setAberto({ ncmId: n.id, tab: 'regra' });
  };

  const situacaoPorNcm = useMemo(() => new Map(itens.map((i) => [i.ncm, i.situacao])), [itens]);
  const descricaoPorPosicao = useMemo(() => {
    const m = new Map<string, string | null>();
    [...itens].sort((a, b) => a.ncmId - b.ncmId).forEach((i) => {
      const p = i.ncm.slice(0, 4);
      if (!m.has(p)) m.set(p, i.descricao);
    });
    return m;
  }, [itens]);
  const adotados = useMemo(() => itens.map((i) => ({ ncmId: i.ncmId, ncm: i.ncm })), [itens]);
  const itemAberto = aberto
    ? itens.find((i) => i.ncmId === aberto.ncmId) ?? (doCatalogo?.ncmId === aberto.ncmId ? doCatalogo : null)
    : null;

  return (
    <>
      {vista === 'adotados' ? (
        <StandardDataTable
          columns={columns}
          data={linhas}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          onRestoreColumns={handleRestoreColumns}
          isLoading={isLoading}
          selectable={false}
          getRowId={(l) => l.id}
          getSubRows={(l) => (l.kind === 'capitulo' ? l.subRows : undefined)}
          expandColumnId="ncm"
          expanded={expanded}
          onExpandedChange={onExpandedChange}
          onRowClick={(l) => (l.kind === 'ncm' ? abrir(l.item.ncmId, 'regra') : alternarCapitulo(l.id))}
          countLabel="NCMs"
          totalCount={visiveis.length}
          emptyMessage={filtrado ? 'Nenhum NCM com esses filtros.' : 'Nenhum NCM adotado.'}
        />
      ) : (
        <CatalogView
          termo={search ?? ''}
          situacaoPorNcm={situacaoPorNcm}
          descricaoPorPosicao={descricaoPorPosicao}
          onAbrir={abrirDoCatalogo}
        />
      )}

      {itemAberto && aberto && (
        <NcmDrawer
          item={itemAberto}
          emissores={emissores}
          adotados={adotados}
          tab={aberto.tab}
          onTabChange={(tab) => setAberto({ ...aberto, tab })}
          onClose={() => setAberto(null)}
          onChanged={recarregar}
          onRemovido={() => {
            setAberto(null);
            recarregar();
          }}
          onFeedback={notificar}
        />
      )}
    </>
  );
}
